import React, { useState, useCallback, useRef, FC } from 'react';
import { useAuth, AppAssets } from '../contexts/AuthContext';
import { useFlight } from '../contexts/FlightContext';
import { XCircleIcon, CheckIcon, SpinnerIcon, ArrowUpIcon } from './Icons';

interface PwaIconWizardProps {
    isOpen: boolean;
    onClose: () => void;
    targetAssetKey?: keyof AppAssets;
}

interface Previews {
    pwaIcon512: string;
    pwaIcon192: string;
    appleTouchIcon: string;
}

// Helper function to resize an image using a canvas
const resizeImage = (imageSrc: string, size: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error('Could not get canvas context'));
      }
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (err) => reject(new Error('Failed to load image for resizing.'));
    img.src = imageSrc;
  });
};

const MaskablePreview: FC<{ src: string; size: number }> = ({ src, size }) => (
    <div className="relative bg-gray-400" style={{ width: size, height: size }}>
      <img src={src} alt="Maskable preview" className="w-full h-full object-cover" />
      <div className="absolute inset-0 flex items-center justify-center" title="This is the 'safe zone' for maskable icons. Ensure your main logo fits inside this circle.">
        <div className="w-[80%] h-[80%] border-2 border-dashed border-white/70 rounded-full"></div>
      </div>
    </div>
);

const PwaIconWizard: React.FC<PwaIconWizardProps> = ({ isOpen, onClose, targetAssetKey }) => {
    const { updateAsset } = useAuth();
    const { showNotification } = useFlight();
    const [sourceImage, setSourceImage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleResetAndClose = useCallback(() => {
        setSourceImage(null);
        setError('');
        setIsLoading(false);
        onClose();
    }, [onClose]);

    const handleFileChange = (file: File | null) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please upload a valid image file (PNG, JPG).');
            return;
        }
        setError('');
        setSourceImage(null);
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result as string;
            const img = new Image();
            img.onload = () => {
                if (img.width < 512 || img.height < 512) {
                    setError('Image too small. Please upload an image that is at least 512x512 pixels.');
                } else if (img.width !== img.height) {
                    setError('Image is not square. Please upload a square image for best results.');
                } else {
                    setSourceImage(result);
                }
            };
            img.src = result;
        };
        reader.readAsDataURL(file);
    };

    const handleProcessAndSave = async () => {
        if (!sourceImage) return;
        setIsLoading(true);
        setError('');
        try {
            const [pwa512, pwa192, apple180] = await Promise.all([
                resizeImage(sourceImage, 512),
                resizeImage(sourceImage, 192),
                resizeImage(sourceImage, 180),
            ]);

            const updatePromises = [
                updateAsset('pwaIcon512', pwa512),
                updateAsset('pwaIcon192', pwa192),
                updateAsset('appleTouchIcon', apple180),
            ];

            if (targetAssetKey) {
                updatePromises.push(updateAsset(targetAssetKey, sourceImage));
            }
            
            await Promise.all(updatePromises);

            showNotification('Icons updated successfully! Refresh may be needed.', 'success');
            setTimeout(handleResetAndClose, 1500);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            setError(`Processing failed: ${errorMessage}`);
            console.error(err);
            setIsLoading(false);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-2xl border border-slate-700 relative">
                <button onClick={handleResetAndClose} className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors">
                    <XCircleIcon className="w-8 h-8" />
                </button>
                <h2 className="text-2xl font-bold text-white mb-4">Icon & PWA Wizard</h2>
                
                {isLoading && (
                    <div className="text-center p-8">
                        <SpinnerIcon className="w-12 h-12 text-white animate-spin-slow mx-auto" />
                        <p className="mt-4 text-lg text-slate-300">Processing and saving icons...</p>
                    </div>
                )}

                {!isLoading && (
                    <>
                        <div className="space-y-4">
                            <input
                                type="file"
                                accept="image/png, image/jpeg"
                                ref={fileInputRef}
                                className="hidden"
                                onChange={(e) => handleFileChange(e.target.files ? e.target.files[0] : null)}
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full bg-slate-700/50 hover:bg-slate-700 border-2 border-dashed border-slate-600 hover:border-blue-500 p-6 rounded-lg text-center transition-colors"
                            >
                                <ArrowUpIcon className="w-10 h-10 mx-auto text-slate-400" />
                                <p className="mt-2 font-semibold text-white">Click to upload App Icon</p>
                                <p className="text-xs text-slate-400">Recommended: Square PNG, at least 512x512px</p>
                            </button>
                            
                            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

                            {sourceImage && (
                                <div className="mt-4 pt-4 border-t border-slate-700">
                                    <h3 className="text-lg font-semibold text-white mb-3 text-center">Previews</h3>
                                    <div className="flex flex-wrap justify-center items-center gap-4 text-center text-xs text-slate-300">
                                        <div>
                                            <p className="font-bold mb-1">Maskable (192px)</p>
                                            <MaskablePreview src={sourceImage} size={96} />
                                        </div>
                                        <div>
                                            <p className="font-bold mb-1">Android (192px)</p>
                                            <img src={sourceImage} alt="192px preview" className="w-24 h-24 rounded-lg bg-slate-700 object-contain"/>
                                        </div>
                                        <div>
                                            <p className="font-bold mb-1">iOS (180px)</p>
                                            <img src={sourceImage} alt="180px preview" className="w-20 h-20 rounded-xl bg-slate-700 object-contain"/>
                                        </div>
                                        <div>
                                            <p className="font-bold mb-1">Splash (512px)</p>
                                            <img src={sourceImage} alt="512px preview" className="w-32 h-32 rounded-2xl bg-slate-700 object-contain"/>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-slate-700">
                            <button onClick={handleResetAndClose} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                            <button
                                onClick={handleProcessAndSave}
                                disabled={!sourceImage}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <CheckIcon className="w-5 h-5" />
                                Process & Save Icons
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PwaIconWizard;